import Vue from "vue";
import { getBrowser } from "@/utils/browser";

export interface AppEndpoint {
  host: string;
  weight?: number;
  channel?: string;
}

const CHANNEL_KEY = "app_config_api_channel";
const LAST_SUCCESS_API_HOST_KEY = "h5_last_success_api_host";
let hasLoggedDomainConfig = false;
/** App 域名来源：bridge=getCurrentDomainConfig，ua=UA 参数兜底 */
let appDomainConfigSource: "bridge" | "ua" | null = null;
/** App getCurrentDomainConfig 原始下发（仅 bridge 成功时） */
let appBridgeDomainConfigRaw: Record<string, unknown> | null = null;
/** App UA 注入兜底参数 */
let appUaDomainConfigRaw: { apiHost?: string; wsHost?: string } | null = null;

export function getSelectedChannel(): string | null {
  return localStorage.getItem(CHANNEL_KEY);
}

export function setSelectedChannel(channel: string | null) {
  if (channel) {
    localStorage.setItem(CHANNEL_KEY, channel);
  } else {
    localStorage.removeItem(CHANNEL_KEY);
  }
}

/** weight 越大越靠前；可按用户选择的 channel 过滤 */
export function resolveHosts(
  list: AppEndpoint[] | undefined,
  channel?: string | null
): string[] {
  if (!list?.length) return [];
  let items = list;
  if (channel) {
    const filtered = list.filter(item => item.channel === channel);
    if (filtered.length) items = filtered;
  }
  return [...items]
    .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0))
    .map(item => (item.host || "").replace(/\/$/, ""))
    .filter(Boolean);
}

function normalizeHost(host?: string): string {
  return (host || "").replace(/\/$/, "");
}

/** config 中的默认 host，不参与「上次成功」逻辑，避免与 getApiHostList 循环调用 */
function getConfigApiHost(): string {
  return normalizeHost((window as any).$config?.VUE_APP_API_HOST);
}

function getConfigWsHost(): string {
  return normalizeHost((window as any).$config?.VUE_APP_WS_HOST);
}

function applyAppConfigLists(
  api?: AppEndpoint[],
  ws?: AppEndpoint[],
  source?: "bridge" | "ua"
) {
  const cfg = (window as any).$config;
  if (!cfg) return;
  cfg.AppConfig = cfg.AppConfig || {};
  if (api?.length) cfg.AppConfig.api = api;
  if (ws?.length) cfg.AppConfig.ws = ws;
  const channel = getSelectedChannel();
  const apiHosts = resolveHosts(cfg.AppConfig.api, channel);
  if (apiHosts[0]) cfg.VUE_APP_API_HOST = apiHosts[0];
  const wsHosts = resolveHosts(cfg.AppConfig.ws, channel);
  if (wsHosts[0]) cfg.VUE_APP_WS_HOST = wsHosts[0];
  if (source) appDomainConfigSource = source;
}

function snapshotAppDomainPayload(data: Record<string, any>): Record<string, unknown> {
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return { ...data };
  }
}

/** 从 App / OSS 形态中取出 api、ws（支持顶层或 AppConfig 包裹） */
export function extractAppDomainEndpoints(data: Record<string, any>) {
  const api = (data.api || data.AppConfig?.api) as AppEndpoint[] | undefined;
  const ws = (data.ws || data.AppConfig?.ws) as AppEndpoint[] | undefined;
  return { api, ws };
}

/** 解析 getCurrentDomainConfig 回调（支持 JSON 字符串、双层 JSON、{ data } 包裹） */
export function parseAppBridgeDomainResponse(res: any): Record<string, any> | null {
  if (res == null || res === "") return null;

  let data: any = res;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (data && typeof data.data === "string") {
    try {
      const inner = JSON.parse(data.data);
      data = { ...data, ...inner };
    } catch {
      /* ignore */
    }
  }
  if (data?.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    data = { ...data, ...data.data };
  }

  const { api, ws } = extractAppDomainEndpoints(data);
  if (!api?.length && !ws?.length) return null;
  return data;
}

/** App 从该版本起 Native 才实现 getCurrentDomainConfig（1.5.35 及以下跳过，避免空等 8s） */
export const MIN_APP_DOMAIN_CONFIG_BRIDGE_VERSION = "1.5.36";

function parseAppVersionParts(version?: string): number[] | null {
  if (!version) return null;
  const parts = version.trim().split(".").map(n => parseInt(n, 10));
  if (!parts.length || parts.some(n => Number.isNaN(n))) return null;
  return parts;
}

/** a >= b 返回 >=0 */
export function compareAppVersion(a: string, b: string): number {
  const pa = parseAppVersionParts(a) || [0];
  const pb = parseAppVersionParts(b) || [0];
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
}

export function isAppDomainConfigBridgeSupported(appVersion?: string): boolean {
  if (!appVersion) return false;
  return compareAppVersion(appVersion, MIN_APP_DOMAIN_CONFIG_BRIDGE_VERSION) >= 0;
}

/** App JsBridge：getCurrentDomainConfig */
export function applyAppDomainConfigFromBridge(data: Record<string, any>): boolean {
  if (!getBrowser().isApp || !data) return false;
  const parsed = parseAppBridgeDomainResponse(data) || data;
  const { api, ws } = extractAppDomainEndpoints(parsed);
  if (!api?.length && !ws?.length) return false;
  appBridgeDomainConfigRaw = snapshotAppDomainPayload(parsed);
  appUaDomainConfigRaw = null;
  applyAppConfigLists(api, ws, "bridge");
  return true;
}

/** App UA 注入 http / wss（JsBridge 不可用时的兜底） */
export function applyAppHostsFromUa(): boolean {
  const browser = getBrowser();
  if (!browser.isApp) return false;

  const apiHost = normalizeHost(browser.apiHost);
  const wsHost = normalizeHost(browser.wsHost);
  if (!apiHost && !wsHost) return false;

  const api: AppEndpoint[] = [];
  const ws: AppEndpoint[] = [];
  if (apiHost) api.push({ host: apiHost, weight: 1, channel: "App" });
  if (wsHost) ws.push({ host: wsHost, weight: 1, channel: "App" });
  appUaDomainConfigRaw = {
    ...(apiHost ? { apiHost } : {}),
    ...(wsHost ? { wsHost } : {})
  };
  appBridgeDomainConfigRaw = null;
  applyAppConfigLists(api, ws, "ua");
  return true;
}

export function isAppHostMode(): boolean {
  if (!getBrowser().isApp) return false;
  const cfg = (window as any).$config?.AppConfig;
  if (cfg?.api?.length || cfg?.ws?.length) return true;
  return !!(getBrowser().apiHost || getBrowser().wsHost);
}

/** 仅 H5 记住上次请求成功的 API host */
export function shouldRememberApiHost(): boolean {
  return !getBrowser().isApp;
}

export function getLastSuccessApiHost(): string | null {
  if (!shouldRememberApiHost()) return null;
  const host = localStorage.getItem(LAST_SUCCESS_API_HOST_KEY);
  return host ? normalizeHost(host) : null;
}

export function setLastSuccessApiHost(host: string) {
  if (!shouldRememberApiHost()) return;
  const h = normalizeHost(host);
  if (h) localStorage.setItem(LAST_SUCCESS_API_HOST_KEY, h);
}

/** OSS / 通道变更后，上次成功 host 不在列表则清除 */
export function resetLastSuccessApiHostIfInvalid() {
  if (!shouldRememberApiHost()) return;
  const last = getLastSuccessApiHost();
  const list = getApiHostList();
  if (last && list.length && !list.includes(last)) {
    localStorage.removeItem(LAST_SUCCESS_API_HOST_KEY);
  }
}

/** H5 优先用上次成功的 host；App 用当前配置 */
export function getPreferredApiHost(): string {
  const list = getApiHostList();
  if (list.length) {
    const last = getLastSuccessApiHost();
    if (last && list.includes(last)) return last;
    return list[0];
  }
  return getConfigApiHost();
}

export function getPreferredApiHostIndex(): number {
  const list = getApiHostList();
  const preferred = getPreferredApiHost();
  const idx = list.indexOf(preferred);
  return idx >= 0 ? idx : 0;
}

/** 单 host 最多尝试 2 次；多 host 最多尝试 list.length 次（末位失败后从 0 再轮） */
export function getMaxApiRetryAttempts(hostList: string[]): number {
  if (!hostList.length) return 0;
  return hostList.length <= 1 ? 2 : hostList.length;
}

export function getNextApiHostIndex(
  currentIndex: number,
  listLength: number
): number {
  if (listLength <= 0) return 0;
  return (currentIndex + 1) % listLength;
}

/** 当前 API 域名（请求默认 baseURL） */
export function getApiHost(): string {
  return getPreferredApiHost();
}

/** 备选 API 域名：H5 来自 OSS；App 通常仅一条 */
export function getApiHostList(): string[] {
  const cfg = (window as any).$config || {};
  const fromOss = resolveHosts(cfg.AppConfig?.api, getSelectedChannel());
  if (fromOss.length) return fromOss;
  const host = getConfigApiHost();
  return host ? [host] : [];
}

export function getWsHost(): string {
  return getConfigWsHost();
}

export function getWsHostList(): string[] {
  const cfg = (window as any).$config || {};
  const fromOss = resolveHosts(cfg.AppConfig?.ws, getSelectedChannel());
  if (fromOss.length) return fromOss;
  const host = getConfigWsHost();
  return host ? [host] : [];
}

export function getWsUrlFromHost(host: string, path: string): string {
  const h = normalizeHost(host);
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${h}${p}`;
}

export function getWsUrl(path: string): string {
  return getWsUrlFromHost(getWsHost(), path);
}

export function getWsUrlList(path: string): string[] {
  return getWsHostList().map(host => getWsUrlFromHost(host, path));
}

function formatEndpointsForLog(list?: AppEndpoint[]) {
  return (list || []).map(item => ({
    host: normalizeHost(item.host),
    weight: item.weight ?? 0,
    channel: item.channel || "-"
  }));
}

function findEndpointChannel(host: string, list?: AppEndpoint[]): string {
  const h = normalizeHost(host);
  const item = (list || []).find(ep => normalizeHost(ep.host) === h);
  return item?.channel || "-";
}

/** App WebView 系统：优先 UA 中 platform=ios|android */
function getAppOsLabel(browser: ReturnType<typeof getBrowser>): string {
  if (!browser.isApp) return "";
  if (browser.isIosApp || browser.isIos) return "iOS";
  if (browser.isAndroidApp || browser.isAndroid) return "Android";
  return "未知";
}

function getConfigSource(): string {
  if (getBrowser().isApp) {
    if (appDomainConfigSource === "bridge") {
      return "APP-getCurrentDomainConfig";
    }
    if (appDomainConfigSource === "ua") return "APP-UA兜底";
    return "APP";
  }
  const cfg = (window as any).$config || {};
  if (cfg.AppConfig?.api?.length) return "OSS";
  return "config.js默认";
}

function describeWsSocket(
  socket: { isOpen?: boolean; url?: string; options?: { url?: string } } | null | undefined,
  label: string
): string {
  if (!socket) return `${label}: 暂未连接wss`;
  if (socket.isOpen && socket.url) return `${label}: 已连接 ${socket.url}`;
  const pending = socket.options?.url || socket.url;
  if (pending) return `${label}: 连接中 ${pending}`;
  return `${label}: 暂未连接wss`;
}

/** 首次业务接口请求成功时打印（dev 环境可见） */
export function logApiDomainConfigOnSuccess() {
  if (hasLoggedDomainConfig) return;
  hasLoggedDomainConfig = true;

  const browser = getBrowser();
  const platform = browser.isApp ? "APP" : "H5";
  const cfg = (window as any).$config || {};

  const apiList = cfg.AppConfig?.api as AppEndpoint[] | undefined;
  const wsList = cfg.AppConfig?.ws as AppEndpoint[] | undefined;
  const currentApiHost = getApiHost();
  const currentWsHost = getWsHost();
  const channelFilter = getSelectedChannel() || "自动";

  const payload: Record<string, unknown> = {
    运行环境: platform,
    配置来源: getConfigSource(),
    接入通道筛选: channelFilter,
    http当前: {
      host: currentApiHost,
      channel: findEndpointChannel(currentApiHost, apiList)
    },
    wss配置: currentWsHost
      ? {
          host: currentWsHost,
          channel: findEndpointChannel(currentWsHost, wsList)
        }
      : "未配置",
    http列表: formatEndpointsForLog(apiList),
    wss列表: formatEndpointsForLog(wsList),
    wss连接: {
      行情tick: describeWsSocket(Vue.prototype.$socket, "tick"),
      订单ordertick: describeWsSocket(Vue.prototype.$transSocket, "ordertick")
    }
  };

  if (platform === "H5") {
    payload.http上次成功 = getLastSuccessApiHost() || "无";
    payload.oss地址 = cfg.VUE_APP_APP_CONFIG_URL;
  } else if (platform === "APP") {
    payload.App系统 = getAppOsLabel(browser);
    payload.App版本 = browser.appVersion || "未知";
    if (appBridgeDomainConfigRaw) {
      payload.APP下发原始数据 = appBridgeDomainConfigRaw;
    } else if (appUaDomainConfigRaw) {
      payload.APP下发原始数据 = { 来源: "UA兜底", ...appUaDomainConfigRaw };
    } else if (appDomainConfigSource === "bridge") {
      payload.APP下发原始数据 = {
        说明: "JsBridge 已生效但原始快照缺失",
        当前AppConfig: cfg.AppConfig
      };
    } else {
      payload.APP下发原始数据 = {
        说明: "JsBridge 未返回有效 api/ws（或未走 getCurrentDomainConfig）",
        当前AppConfig: cfg.AppConfig
      };
    }
    if (browser.apiHost) payload.httpUA参数 = normalizeHost(browser.apiHost);
    if (browser.wsHost) payload.wssUA参数 = normalizeHost(browser.wsHost);
  }

  (window as any).__DOMAIN_CONFIG_LOG__ = payload;
  console.log("[域名配置] 当前生效（首包接口已成功）", payload);
}

/** 仅允许重复打印域名日志；快照与来源在下次 apply 时覆盖 */
export function resetApiDomainConfigLog() {
  hasLoggedDomainConfig = false;
}

/** OSS 失败时用 config.js 默认值构造单条 AppConfig */
export function ensureFallbackAppConfig() {
  const cfg = (window as any).$config;
  if (!cfg) return;
  cfg.AppConfig = cfg.AppConfig || {};
  if (!cfg.AppConfig.api?.length && cfg.VUE_APP_API_HOST) {
    cfg.AppConfig.api = [{ host: cfg.VUE_APP_API_HOST, weight: 1 }];
  }
  if (!cfg.AppConfig.ws?.length && cfg.VUE_APP_WS_HOST) {
    cfg.AppConfig.ws = [{ host: cfg.VUE_APP_WS_HOST, weight: 1 }];
  }
  resetLastSuccessApiHostIfInvalid();
}
